import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import CollectionMoveCopy from '@components/modals/CollectionMoveCopy';
import PermissionOfMenus from '@components/PermissionOfMenus';

import { TopNavLink } from '@layouts/partials/TopNavLink';

import { setCurrentActivePage } from '@store/actions/ActivePageAction';
import {
	clearAllChats,
	storeEntityIdForListPage,
	storeNewMsg,
	storeNewUnreadMention
} from '@store/actions/ChatAction';
import { _storeData as _storeDesignData } from '@store/actions/DesignAction';
import { clearGlobalFilter } from '@store/actions/FilterAction';
import { setActiveTab } from '@store/actions/sidebar';
import { setFilters } from '@store/common/action';
import { initialFilters } from '@store/common/reducer';
import { removeDesignAfterMove } from '@store/design/action';

import ApiClient from '@lib/apiClient';

import { ACCESS_TOKEN, getUserInfo } from '@utils/Util';

import { ALL_TOP_BAR_MENUS, getSocketChannel, ROUTES, TOP_BAR_MORE_MENUS } from '@/constants/constant';

import CommonImage from '@/components/CommonImage';
import { toastError } from '@/components/Toast';
// import { useGetMyDashboardTasksQuery } from '@/features/task-configuration/api/taskConfigurationApi';

const TopBar = ({ stompClient, detailPageId }) => {
	const [state, setState] = useState({
		activeTab: '',
		permissions: [],
		showSubmenu: false,
		orderOfMenus: [],
		channelList: [],
		copyOrMoveList: {},
		showMobileMenu: false,
		completedIdsAfterCopyOrMove: []
	});
	const [selectedSubMenuKey, setSelectedSubMenuKey] = useState('');
	const storeActiveTab = useSelector((store) => store.sidebar.activeTab);
	const oldMessages = useSelector((store) => store.chatReducer);
	const activePage = useSelector((store) => store.activePageReducer);
	const dispatch = useDispatch();
	const location = useLocation();
	const subMenuRef = useRef();
	const subMenuRootRef = useRef();

	// Query for My Dashboard tasks
	// const { data: myDashboardTasks = [] } = useGetMyDashboardTasksQuery();

	// Check if My Dashboard should be shown
	// const shouldShowMyDashboard = !isAdmin();

	const onProcessConversationMessage = (postResponse) => {
		if (
			activePage.type === 'ORDER_DASHBOARD_UNREAD_POST_DELIVERABLE' &&
			oldMessages &&
			Array.isArray(oldMessages)
		) {
			if (postResponse?.postType !== 'TASK_COMPLETE' && postResponse?.postType !== 'TASK_APPROVE') {
				let mergedArray = [...oldMessages];
				mergedArray.push(postResponse.entityId);
				dispatch(storeNewMsg([...new Set(mergedArray)]));
			}
		}
		if (
			postResponse.entityId === parseInt(activePage.id) &&
			postResponse.conversationTopic === activePage.type &&
			activePage.pageType === 'DETAILS'
		) {
			dispatch(storeNewMsg(postResponse));
		}
		if (postResponse.conversationTopic === activePage.type && activePage.pageType === 'LIST') {
			dispatch(storeEntityIdForListPage(postResponse.entityId));
		}
	};

	const onStoreUnreadMention = (postResponse) => {
		dispatch(storeNewUnreadMention(postResponse?.data));
	};

	const onClickCrossIcon = (id) => {
		stompClient.disconnect(function () {});
		let oldData = { ...state.copyOrMoveList };
		delete oldData[id];
		const closedIds = [...state.completedIdsAfterCopyOrMove, id];
		// keep only closed IDs to localStorage
		localStorage.setItem('closedCopyMoveIds', JSON.stringify(closedIds));
		setState((prev) => ({
			...prev,
			copyOrMoveList: oldData,
			completedIdsAfterCopyOrMove: closedIds
		}));
	};

	const onProcessProductCopyOrMove = (postResponse) => {
		// Get closed IDs from localStorage
		const closedCopyMoveIds = JSON.parse(localStorage.getItem('closedCopyMoveIds') || '[]');
		// Don't process if this item was previously closed
		if (closedCopyMoveIds.includes(postResponse?.data?.id)) {
			return;
		}
		let data = state.copyOrMoveList[postResponse?.data.id];
		if (data) {
			if (postResponse?.data?.isCopyRunning) {
				let oldData = { ...state.copyOrMoveList };
				delete oldData[postResponse?.data.id];
				setState((prev) => ({
					...prev,
					copyOrMoveList: {
						...state.copyOrMoveList,
						[postResponse?.data.id]: postResponse?.data
					}
				}));
			} else {
				let oldData = { ...state.copyOrMoveList };
				setState((prev) => ({
					...prev,
					copyOrMoveList: {
						...oldData
					},
					completedIdsAfterCopyOrMove: [
						...state.completedIdsAfterCopyOrMove,
						postResponse?.data?.id
					]
				}));
				dispatch(
					removeDesignAfterMove({
						opType: postResponse?.data?.opType,
						collectionId: postResponse?.data?.collectionId,
						productIds: postResponse?.data?.productIds
					})
				);
			}
		} else {
			setState((prev) => ({
				...prev,
				copyOrMoveList: {
					...state.copyOrMoveList,
					[postResponse?.data.id]: postResponse?.data
				}
			}));
		}
	};

	const onConnected = async () => {
		const channel = getSocketChannel(getUserInfo().id);
		stompClient.subscribe(
			channel,
			(payload) => {
				const message = JSON.parse(payload.body);
				if (message.messageType === 'CONVERSATION') {
					onProcessConversationMessage(message.content);
				} else if (message.messageType === 'INFO') {
					switch (message.content.type) {
						case 'ORDER_DASHBOARD_UNREAD_POST_DELIVERABLE':
							if (activePage.type === 'ORDER_DASHBOARD_UNREAD_POST_DELIVERABLE') {
								let mergedArray = [];
								if (oldMessages && Array.isArray(oldMessages)) {
									mergedArray = [...oldMessages, ...message.content.data];
								} else {
									mergedArray = [...message.content.data];
								}
								dispatch(storeNewMsg([...new Set(mergedArray)]));
							}
							break;
						case 'UNREAD_MENTION':
							onStoreUnreadMention(message.content);
							break;
						case 'PRODUCT_COPY_START':
						case 'PRODUCT_COPY_PROGRESS':
						case 'PRODUCT_COPY_END':
							onProcessProductCopyOrMove(message.content);
							break;
						default:
							break;
					}
				}
			},
			{ jwt: ACCESS_TOKEN() }
		);
	};

	const onError = (error) => {
		if (error?.headers?.message === '401') {
			stompClient.disconnect(function () {});
		}
	};

	const registerConnection = async () => {
		stompClient.connect({ jwt: ACCESS_TOKEN() }, onConnected, onError);
	};

	const handleSubMenuHide = (event) => {
		if (
			subMenuRef &&
			!subMenuRef?.current?.contains(event?.target) &&
			subMenuRootRef &&
			!subMenuRootRef?.current?.contains(event?.target)
		) {
			setState((prev) => ({
				...prev,
				showSubmenu: false
			}));
		}
	};

	const onSetActiveTab = () => {
		let tab = '';
		let extractedTab = location.pathname.split('/')[1];
		let extractedSecondTab = location.pathname.split('/')[2];
		const params = new URLSearchParams(location.search);
		const STATUS_TAB = params.get('status') || '';
		const SECOND_TABS = ['request', 'explore-designs', 'general-settings'];
		if (SECOND_TABS.includes(extractedSecondTab)) {
			tab = extractedSecondTab;
		} else if (extractedTab === 'brand' && extractedSecondTab === 'profile') {
			tab = 'crm';
		} else if (STATUS_TAB === 'Published') {
			tab = 'nitex-studio';
		} else {
			tab = extractedTab;
		}
		let selectedTabName = tab;
		switch (tab) {
			case 'orders':
			case 'order':
				selectedTabName = 'orders';
				break;
			case 'my-dashboard':
				selectedTabName = 'my-dashboard';
				break;
			case 'my-profile':
				selectedTabName = 'profile';
				break;
			case 'change-password':
				selectedTabName = 'changePassword';
				break;
			case 'my-preferences':
				selectedTabName = 'preferences';
				break;
			case 'buyer-requests':
			case 'request':
				selectedTabName = 'buyer-requests';
				break;
			default:
				selectedTabName = tab;
				break;
		}
		setState((prev) => ({ ...prev, activeTab: selectedTabName }));
	};

	const setActivePageDetails = (pageName, id = null) => {
		if (pageName && id) {
			if (pageName === 'quote' || pageName === 'costing') {
				dispatch(
					setCurrentActivePage({
						id: id,
						type: 'COSTING_CONVERSATION',
						pageType: 'DETAILS'
					})
				);
			} else if (pageName === 'sample') {
				dispatch(
					setCurrentActivePage({
						id: id,
						type: 'SAMPLE_CONVERSATION',
						pageType: 'DETAILS'
					})
				);
			} else if (pageName === 'collections') {
				dispatch(
					setCurrentActivePage({
						id: id,
						type: 'COLLECTION_CONVERSATION',
						pageType: 'DETAILS'
					})
				);
			} else {
				dispatch(
					setCurrentActivePage({
						id: null,
						type: '',
						pageType: 'DETAILS'
					})
				);
			}
		} else {
			let params = new URLSearchParams(location.search);
			if (pageName === 'costing') {
				dispatch(
					setCurrentActivePage({
						type: 'COSTING_CONVERSATION',
						pageType: 'LIST'
					})
				);
			} else if (pageName === 'sample') {
				dispatch(
					setCurrentActivePage({
						type: 'SAMPLE_CONVERSATION',
						pageType: 'LIST'
					})
				);
			} else if (pageName === 'orders' && params.get('active') === 'ORDER_DASHBOARD') {
				dispatch(
					setCurrentActivePage({
						id: null,
						type: 'ORDER_DASHBOARD_UNREAD_POST_DELIVERABLE',
						pageType: 'DETAILS'
					})
				);
			} else if (pageName === 'collections') {
				dispatch(
					setCurrentActivePage({
						type: 'COLLECTION_CONVERSATION',
						pageType: 'LIST'
					})
				);
			} else if (pageName === 'nitex-studio') {
				dispatch(
					setCurrentActivePage({
						type: 'COLLECTION_CONVERSATION',
						pageType: 'LIST'
					})
				);
			} else {
				dispatch(
					setCurrentActivePage({
						id: null,
						type: '',
						pageType: 'DETAILS'
					})
				);
			}
		}
	};

	const getRunningCopyList = async () => {
		try {
			const { data = [] } = await ApiClient.GET('getRunningCopyList');
			// Get closed IDs from localStorage
			const closedCopyMoveIds = JSON.parse(localStorage.getItem('closedCopyMoveIds') || '[]');
			// Filter out closed items from API response
			const filteredData = data.filter((item) => !closedCopyMoveIds.includes(item.id));
			if (filteredData?.length > 0) {
				filteredData.map((item) => onProcessProductCopyOrMove({ data: item }));
			}
			// Restore closed IDs state from localStorage
			setState((prev) => ({
				...prev,
				completedIdsAfterCopyOrMove: closedCopyMoveIds
			}));
		} catch (error) {
			toastError(error?.data?.message || 'Something went wrong!');
		}
	};

	useEffect(() => {
		window.addEventListener('mousedown', handleSubMenuHide);
		setState((prev) => ({ ...prev, orderOfMenus: JSON.parse(localStorage.getItem('orderOfMenus')) }));
		getRunningCopyList();

		let userInfo = localStorage.getItem('userInfo');
		if (userInfo) {
			userInfo = JSON.parse(userInfo);
		} else {
			userInfo = {};
		}
		setState((prev) => ({
			...prev,
			permissions: userInfo.permissions ? userInfo.permissions : []
		}));
		registerConnection();
		setActivePageDetails(location.pathname.split('/')[1], detailPageId);

		return () => {
			dispatch(
				removeDesignAfterMove({
					collectionId: null,
					productIds: []
				})
			);
			window.removeEventListener('mousedown', handleSubMenuHide);
		};
	}, []);

	useEffect(() => {
		onSetActiveTab();
		dispatch(_storeDesignData('selectedProductIds', []));

		dispatch(clearAllChats());
		setActivePageDetails(location.pathname.split('/')[1], detailPageId);
	}, [storeActiveTab, location]);

	const onSubmenuClick = (pathName) => {
		dispatch(clearGlobalFilter());
		dispatch(setFilters(initialFilters));
		setState((prev) => ({
			...prev,
			showSubmenu: false
		}));
		dispatch(setActiveTab(pathName));
	};

	const getSubMenuPermissionList = (subMenus) => {
		let permissionList = [];
		subMenus.forEach((item) => {
			permissionList = [...permissionList, ...item.permissions];
		});
		return permissionList;
	};

	const renderNavMenu = (key) => {
		const selectedNav = ALL_TOP_BAR_MENUS.find((item) => item.key === key);

		if (selectedNav?.key) {
			return (
				<PermissionOfMenus allowedPermission={selectedNav.permission}>
					<TopNavLink
						to={selectedNav.route}
						classes={`${state?.activeTab === selectedNav.activeTabKey ? 'active' : ''} ${selectedNav.activeTabKey === selectedSubMenuKey ? 'active-submenu' : ''}`}
						title={selectedNav.title}
						onClick={(to) => {
							if (to !== '#') {
								dispatch(setActiveTab(to));
								setState((prev) => ({ ...prev, showMobileMenu: false }));
							} else if (selectedNav?.subMenus?.length > 0) {
								setSelectedSubMenuKey((prev) =>
									prev === selectedNav.activeTabKey ? '' : selectedNav.activeTabKey
								);
							}
						}}
					>
						{selectedNav?.subMenus?.length > 0 && (
							<PermissionOfMenus
								key={selectedNav.title}
								allowedMultiPermissions={getSubMenuPermissionList(selectedNav.subMenus)}
								checkType="MULTIPLE"
							>
								<ul className="sub-menu">
									{selectedNav.subMenus.map((menu) => {
										return (
											<React.Fragment key={menu.key}>
												{renderSubMenu(menu)}
											</React.Fragment>
										);
									})}
								</ul>
							</PermissionOfMenus>
						)}
					</TopNavLink>
				</PermissionOfMenus>
			);
		} else {
			return <></>;
		}
	};

	const renderLogo = () => {
		// For non-admin users, default to My Dashboard, otherwise use the first menu item
		const defaultRoute = ROUTES[orderOfMenus[0]];

		return (
			<div className="logo">
				<Link to={defaultRoute} className="logo-expanded">
					<CommonImage src={`/icons/nitex-logo.svg`} alt="logo" />
				</Link>
			</div>
		);
	};

	const renderSubMenu = (menu, isRequestForMoreMenu = false) => {
		const permissionProps = menu?.permissions
			? { allowedMultiPermissions: menu.permissions, checkType: 'MULTIPLE' }
			: { allowedPermission: menu.permission };

		// Add customPermissionType if it exists
		if (menu?.customPermissionType) {
			permissionProps.customPermissionType = menu.customPermissionType;
		}

		return (
			<PermissionOfMenus
				key={menu.key}
				{...permissionProps}
				isRequestForMoreMenu={isRequestForMoreMenu}
			>
				<li className={menu?.subMenus?.length > 0 ? 'sub-menu-item' : ''}>
					<Link
						to={menu.to}
						onClick={() => {
							if (menu.to !== '#') {
								onSubmenuClick(menu.to);
								setState((prev) => ({ ...prev, showMobileMenu: false }));
							}
						}}
						className={activeTab === menu.key ? 'active' : ''}
					>
						{menu.label}
					</Link>
					{menu?.subMenus?.length > 0 && (
						<ul className="child-menu">
							{menu.subMenus.map((firstLayer) => {
								return (
									<React.Fragment key={firstLayer.key}>
										{renderSubMenu(firstLayer, isRequestForMoreMenu)}
									</React.Fragment>
								);
							})}
						</ul>
					)}
				</li>
			</PermissionOfMenus>
		);
	};

	const renderMoreMenu = () => {
		const moreMenuList = [...(getUserInfo()?.moreMenus || [])];
		return (
			<>
				{moreMenuList.length > 0 && (
					<li>
						<span>More</span>
						<ul className="sub-menu">
							{TOP_BAR_MORE_MENUS.map((menu) => {
								return (
									<React.Fragment key={menu.key}>
										{renderSubMenu(menu, true)}
									</React.Fragment>
								);
							})}
						</ul>
					</li>
				)}
			</>
		);
	};

	let { orderOfMenus, activeTab, completedIdsAfterCopyOrMove, showMobileMenu } = state;
	const selectedList = Object.values(state.copyOrMoveList);

	return (
		<>
			{renderLogo()}
			<div className="mobile-menu-controller">
				<div
					className="hamburger cursor-pointer ml-3"
					onClick={() => setState((prev) => ({ ...prev, showMobileMenu: true }))}
				>
					<img width="20px" src={`${process.env.PUBLIC_URL}/icons/hamburger.svg`} alt="view" />
				</div>
			</div>

			<div className={showMobileMenu ? 'main-nav mobile-menu' : 'main-nav'}>
				<div className="hamburger-header justify-content-between align-items-center">
					{renderLogo()}
					<span
						className="close-icon cursor-pointer"
						onClick={() => setState((prev) => ({ ...prev, showMobileMenu: false }))}
					>
						<img src={`${process.env.PUBLIC_URL}/icons/close-icon.svg`} alt="view" />
					</span>
				</div>

				<ul>
					{/* Conditionally render My Dashboard as first item */}
					{/* {shouldShowMyDashboard && (
						<li
							className={state?.activeTab === 'my-dashboard' ? 'active' : ''}
							onClick={() => {
								dispatch(clearGlobalFilter());
								dispatch(setActiveTab('my-dashboard'));
								setState((prev) => ({ ...prev, showMobileMenu: false }));
							}}
						>
							<Link to={'/my-dashboard'}>My Dashboard</Link>
						</li>
					)} */}

					{activeTab &&
						orderOfMenus?.map((item) => {
							return (
								<React.Fragment key={`nav_menu_${item}`}>
									{item !== 'MENU_DESIGNS' && renderNavMenu(item)}
								</React.Fragment>
							);
						})}

					<li
						className={state?.activeTab === 'my-project' ? 'active' : ''}
						onClick={() => {
							dispatch(clearGlobalFilter());
							dispatch(setActiveTab('my-project'));
							setState((prev) => ({ ...prev, showMobileMenu: false }));
						}}
					>
						<Link to={'/my-project'}>My Projects</Link>
					</li>
					{renderMoreMenu()}
				</ul>

				{selectedList?.length > 0 &&
					selectedList?.map((item, index) => {
						return (
							<div key={item.id}>
								<CollectionMoveCopy
									content={item}
									index={index}
									onClickCrossIcon={() => onClickCrossIcon(item.id)}
									completedIds={completedIdsAfterCopyOrMove}
								/>
							</div>
						);
					})}
			</div>
		</>
	);
};

export default TopBar;
